import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SignIn, SignedIn, SignedOut, useAuth, useClerk } from '@clerk/clerk-react';
import './App.css';
import './pages/LoginPage.css';
import Sidebar from './components/Sidebar';
import IdleTimeout from './components/IdleTimeout';
import EligibilityPage from './pages/EligibilityPage';
import ClaimsLedgerPage from './pages/ClaimsLedgerPage';
import PracticesPage from './pages/PracticesPage';
import PracticeDetailsPage from './pages/PracticeDetailsPage';
import PaymentsPage from './pages/PaymentsPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ARPage from './pages/ARPage';
import MissionControlPage from './pages/MissionControlPage';
import RulesPage from './pages/RulesPage';

const API_URL = process.env.REACT_APP_API_URL || '';

// --- URL <-> navigation-state sync (Option A: native History API, no router) ---
// Atlas has no router; navigation is two state vars (selectedPractice + activePage).
// We mirror those to the URL path so the browser Back/Forward buttons and deep
// links work. Backend already serves index.html for unknown paths (Flask 404
// handler), so a hard refresh on these paths boots the app correctly.
const VALID_PAGES = [
  'dashboard', 'eligibility', 'claims', 'payments',
  'ar', 'practice-details', 'appointments', 'admin', 'mission-control', 'rules',
];

function stateToPath(selectedPractice, activePage) {
  if (selectedPractice) return `/p/${selectedPractice.id}/${activePage}`;
  if (activePage === 'admin') return '/admin';
  // Spans every practice, so it has a path of its own rather than one under /p/<id>.
  if (activePage === 'mission-control') return '/mission-control';
  if (activePage === 'rules') return '/rules';
  return '/practices';
}

function parsePath(pathname) {
  const parts = (pathname || '').split('/').filter(Boolean);
  if (parts[0] === 'p' && parts[1]) {
    const id = parseInt(parts[1], 10);
    const page = VALID_PAGES.includes(parts[2]) ? parts[2] : 'dashboard';
    return { practiceId: Number.isNaN(id) ? null : id, page };
  }
  if (parts[0] === 'admin') return { practiceId: null, page: 'admin' };
  if (parts[0] === 'mission-control') return { practiceId: null, page: 'mission-control' };
  if (parts[0] === 'rules') return { practiceId: null, page: 'rules' };
  return { practiceId: null, page: null };
}

function App({ idleTimeoutMinutes = 15, idleWarningSeconds = 60 }) {
  const [activePage, setActivePage] = useState('eligibility');
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  // Set to 'no_local_account' when Clerk has a valid session but the local
  // backend has no matching User row (e.g. someone signed into Clerk but was
  // never invited to Atlas). Surfaces an error screen with a Sign-out button
  // instead of an infinite spinner.
  const [authError, setAuthError] = useState(null);
  const [practices, setPractices] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [resetToken, setResetToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset_token') || null;
  });
  const [inviteToken, setInviteToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('invite_token') || null;
  });
  const [tokenUid, setTokenUid] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('uid') || null;
  });
  const selectedPracticeRef = useRef(null);

  // URL-sync bookkeeping. `restored` flips true after the initial URL->state
  // restore so the write effect and auto-select don't run prematurely.
  // `firstWrite` makes the first URL alignment a replaceState (no junk history
  // entry); every navigation after that pushes so Back/Forward walk the app.
  const [restored, setRestored] = useState(false);
  const firstWrite = useRef(true);

  // Keep ref in sync with state (avoids stale closures in loadPractices)
  useEffect(() => {
    selectedPracticeRef.current = selectedPractice;
  }, [selectedPractice]);

  // Derived role helpers
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
  const isSuperAdmin = user && user.role === 'super_admin';
  const isReadOnly = user && user.role === 'client';

  // Auth: Clerk-only. Legacy cookie session path was removed along with the
  // /api/auth/* routes.
  const { isLoaded: clerkLoaded, isSignedIn: clerkSignedIn } = useAuth();
  const clerk = useClerk();

  useEffect(() => {
    if (!clerkLoaded) return; // wait for Clerk to initialise

    const fetchMe = async () => {
      const response = await fetch(`${API_URL}/api/users/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) return { ok: true, user: data.user };
      }
      return { ok: false };
    };

    const run = async () => {
      setAuthError(null);
      if (!clerkSignedIn) {
        setCheckingAuth(false);
        return;
      }
      try {
        // First attempt.
        let result = await fetchMe();
        // Retry once after a short delay as a safety net — the backend's
        // auto-claim fallback (now reading email from the JWT claim rather
        // than via a Clerk API round-trip) normally resolves the webhook
        // race inline, but keep this for cold-start edge cases.
        if (!result.ok) {
          await new Promise(r => setTimeout(r, 1500));
          result = await fetchMe();
        }
        if (result.ok) {
          setUser(result.user);
        } else {
          setAuthError('no_local_account');
        }
      } catch (err) {
        setAuthError('no_local_account');
      } finally {
        setCheckingAuth(false);
      }
    };

    run();
  }, [clerkLoaded, clerkSignedIn]);

  // Load practices when user is authenticated
  const loadPractices = useCallback(async () => {
    const response = await fetch(`${API_URL}/api/practices`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Failed to load practices: ${response.status}`);
    }
    const data = await response.json();
    setPractices(data.practices || []);
    // If a practice is selected, refresh its full details
    const current = selectedPracticeRef.current;
    if (current) {
      try {
        const fullResp = await fetch(`${API_URL}/api/practices/${current.id}`, {
          credentials: 'include',
        });
        if (fullResp.ok) {
          const fullData = await fullResp.json();
          setSelectedPractice(fullData);
        }
      } catch (err) {
        // Non-critical — practice list already loaded
      }
    }
  }, []);

  const [practicesLoaded, setPracticesLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const load = async () => {
      setPracticesLoaded(false);
      // Retry up to 2 times with a short delay (handles session not yet established)
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await loadPractices();
          if (!cancelled) setPracticesLoaded(true);
          return;
        } catch (err) {
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
          }
        }
      }
      // All retries failed — still mark loaded so user sees empty state, not infinite spinner
      if (!cancelled) setPracticesLoaded(true);
    };

    load();
    return () => { cancelled = true; };
  }, [user, loadPractices]);

  // Auto-select the user's single practice — no reason to show a one-option
  // picker. Previously this skipped super_admin, but that just meant a flash
  // of the practices page on reload for single-practice admins. Admins can
  // still click "All Practices" in the sidebar to get back to the picker.
  useEffect(() => {
    if (restored && practicesLoaded && !selectedPractice && practices.length === 1) {
      handleSelectPractice(practices[0]);
    }
  }, [restored, practicesLoaded, practices, selectedPractice]); // eslint-disable-line

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    // Clerk-only. signOut revokes the current JWT and clears Clerk's session
    // cookie; after that, <ClerkProvider>'s afterSignOutUrl sends the user
    // back to the sign-in screen.
    try {
      await clerk.signOut();
    } catch (err) {
      // Ignore signOut errors — proceed to clear local state regardless.
    }
    setUser(null);
    setSelectedPractice(null);
  };

  const fetchFullPractice = async (practiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/practices/${practiceId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      // Fall through
    }
    return null;
  };

  const handleSelectPractice = async (practice) => {
    const full = await fetchFullPractice(practice.id);
    setSelectedPractice(full || practice);
    setActivePage('dashboard');
  };

  const handlePracticeChange = async (practice) => {
    const full = await fetchFullPractice(practice.id);
    const next = full || practice;
    setSelectedPractice(next);
  };

  const handleBackToPractices = () => {
    setSelectedPractice(null);
    // If the user was on the admin page (selectedPractice already null), also
    // reset activePage so the PracticesPage actually renders. Without this,
    // clicking "All Practices" from the admin page leaves activePage='admin'
    // and the admin branch keeps winning the render.
    setActivePage('dashboard');
  };

  const handleResetComplete = () => {
    setResetToken(null);
    setInviteToken(null);
    setTokenUid(null);
    window.history.replaceState({}, '', window.location.pathname);
  };

  // --- URL <-> navigation-state sync ---

  // Restore navigation state FROM the URL once practices have loaded (deep
  // links, hard refresh). Runs once; takes precedence over the single-practice
  // auto-select above. A practice path carries only the id, so we fetch the
  // full practice via the existing fetchFullPractice helper.
  useEffect(() => {
    if (!user || !practicesLoaded || restored) return;
    const { practiceId, page } = parsePath(window.location.pathname);
    if (practiceId != null) {
      const match = practices.find((p) => p.id === practiceId);
      if (match) {
        (async () => {
          const full = await fetchFullPractice(practiceId);
          setSelectedPractice(full || match);
          if (page) setActivePage(page);
          firstWrite.current = false; // URL already matches; later navs push
          setRestored(true);
        })();
        return;
      }
    }
    // No practice in the URL (or no access to it): keep default behavior.
    if (page === 'admin' && isAdmin) setActivePage('admin');
    setRestored(true);
  }, [user, practicesLoaded, practices, restored, isAdmin]); // eslint-disable-line

  // Mirror navigation state -> URL. First alignment replaces (no junk entry);
  // subsequent navigations push so Back/Forward walk the app.
  useEffect(() => {
    if (!user || !restored || resetToken || inviteToken) return;
    const target = stateToPath(selectedPractice, activePage);
    if (target !== window.location.pathname) {
      if (firstWrite.current) {
        window.history.replaceState({}, '', target);
      } else {
        window.history.pushState({}, '', target);
      }
      firstWrite.current = false;
    }
  }, [user, restored, selectedPractice, activePage, resetToken, inviteToken]);

  // Back/forward: reconcile state from the URL. The browser has already updated
  // location, so the write effect computes an equal path and won't re-push.
  useEffect(() => {
    if (!user) return;
    const onPop = () => {
      const { practiceId, page } = parsePath(window.location.pathname);
      if (practiceId != null) {
        const match = practices.find((p) => p.id === practiceId);
        setSelectedPractice((current) =>
          current && current.id === practiceId ? current : (match || current)
        );
        if (match) {
          fetchFullPractice(practiceId).then((full) => {
            if (full) setSelectedPractice(full);
          });
        }
        if (page) setActivePage(page);
      } else {
        setSelectedPractice(null);
        setActivePage(page === 'admin' ? 'admin' : 'dashboard');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [user, practices]); // eslint-disable-line

  // Idle auto-logout, mounted in every authenticated view below. The warning
  // renders via a portal, so dropping it anywhere is fine; it's enabled only
  // once the user is authenticated.
  const idleGuard = (
    <IdleTimeout
      enabled={!!user}
      timeoutMs={idleTimeoutMinutes * 60 * 1000}
      warningMs={idleWarningSeconds * 1000}
      onTimeout={handleLogout}
    />
  );

  // Show loading while Clerk initializes or while checking auth.
  if (!clerkLoaded || checkingAuth) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Clerk session exists but backend has no matching local user — show a
  // clear error with a Sign-out button so the user can escape the state.
  if (!user && authError === 'no_local_account') {
    return (
      <div className="login-container">
        <div className="login-box auth-error">
          <p className="auth-error-message">
            The account you signed in with doesn't have access to Atlas.
            An administrator needs to invite you before you can sign in.
          </p>
          <button className="login-button" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated by either path → show Clerk's hosted sign-in component.
  // Self-signup is disabled in the Clerk dashboard (Restrictions → invitation
  // only), so this surface only allows signing in or completing an invite link.
  if (!user) {
    return (
      <div className="login-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <SignedOut>
          <SignIn
            routing="hash"
            appearance={{
              elements: {
                // Atlas is invite-only. Hide Clerk's "Don't have an account?
                // Sign up" footer link — even though Clerk's Restrictions
                // mode should also be set to invitation-only in the dashboard
                // (belt-and-suspenders).
                footerAction: { display: 'none' },
              },
            }}
          />
        </SignedOut>
        <SignedIn>
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        </SignedIn>
      </div>
    );
  }

  // Show loading while practices are being fetched, OR while the single-
  // practice auto-select is in flight (prevents a brief flash of the
  // practices picker before the app shell renders).
  const autoSelectPending =
    practicesLoaded && !selectedPractice && practices.length === 1;
  if (!practicesLoaded || autoSelectPending) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Show practices landing page if no practice selected
  if (!selectedPractice) {
    // Admin is accessible without a practice selected (for any admin), and so is Mission
    // Control — it spans every practice the user can see and deliberately takes no
    // selectedPractice, so forcing a drill-into-one-practice first made the page unreachable
    // from the Practices landing page.
    if (((activePage === 'admin' || activePage === 'rules') && isAdmin) || (activePage === 'mission-control' && isSuperAdmin)) {
      return (
        <div className="app">
          <Sidebar
            activePage={activePage}
            onPageChange={setActivePage}
            user={user}
            onLogout={handleLogout}
            practices={practices}
            selectedPractice={null}
            onPracticeChange={handlePracticeChange}
            onBackToPractices={handleBackToPractices}
          />
          <main className="main-content">
            {activePage === 'mission-control'
              ? <MissionControlPage />
              : activePage === 'rules'
              ? <RulesPage practices={practices} isSuperAdmin={isSuperAdmin} />
              : <AdminPage practices={practices} />}
          </main>
          {idleGuard}
        </div>
      );
    }
    return (
      <>
        {idleGuard}
        <PracticesPage
          practices={practices}
          onSelectPractice={handleSelectPractice}
          onPracticesUpdated={loadPractices}
          onGoToAdmin={() => setActivePage('admin')}
          onGoToMissionControl={() => setActivePage('mission-control')}
          userRole={user.role}
        />
      </>
    );
  }

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard':
        return <DashboardPage selectedPractice={selectedPractice} isAdmin={isAdmin} />;
      case 'eligibility':
        return <EligibilityPage selectedPractice={selectedPractice} />;
      case 'practice-details':
        return <PracticeDetailsPage selectedPractice={selectedPractice} onPracticeUpdated={loadPractices} readOnly={isReadOnly} isAdmin={isAdmin} />;
      case 'claims':
        return <ClaimsLedgerPage selectedPractice={selectedPractice} readOnly={isReadOnly} isAdmin={isAdmin} onPageChange={setActivePage} />;
      case 'payments':
        return <PaymentsPage selectedPractice={selectedPractice} readOnly={isReadOnly} isAdmin={isAdmin} />;
      case 'ar':
        return <ARPage selectedPractice={selectedPractice} readOnly={isReadOnly} />;
      // ALWAYS the cross-practice grid, even with a practice open. It sits beside Admin as a
      // supervision page rather than a page about the selected practice, so scoping it to
      // whichever practice happened to be selected would make the same menu item mean two
      // different things. Deliberately passes no selectedPractice, which is what makes the
      // page call /api/mission-control rather than the per-practice endpoint.
      case 'mission-control':
        return isSuperAdmin ? <MissionControlPage /> : <DashboardPage selectedPractice={selectedPractice} />;
      // Cross-practice like Mission Control: rules attach to payers/plans, not the
      // open practice, so it deliberately takes no selectedPractice.
      case 'rules':
        return isAdmin ? <RulesPage practices={practices} isSuperAdmin={isSuperAdmin} /> : <DashboardPage selectedPractice={selectedPractice} isAdmin={isAdmin} />;
      case 'appointments':
        return isAdmin ? <AppointmentsPage selectedPractice={selectedPractice} /> : <DashboardPage selectedPractice={selectedPractice} isAdmin={isAdmin} />;
      case 'admin':
        return isSuperAdmin ? <AdminPage practices={practices} /> : <DashboardPage selectedPractice={selectedPractice} isAdmin={isAdmin} />;
      default:
        return <DashboardPage selectedPractice={selectedPractice} isAdmin={isAdmin} />;
    }
  };

  return (
    <div className="app">
      <Sidebar
        activePage={activePage}
        onPageChange={setActivePage}
        user={user}
        onLogout={handleLogout}
        practices={practices}
        selectedPractice={selectedPractice}
        onPracticeChange={handlePracticeChange}
        onPracticesUpdated={loadPractices}
        onBackToPractices={handleBackToPractices}
      />
      <main className="main-content">
        {renderPage()}
      </main>
      {idleGuard}
    </div>
  );
}

export default App;
